import { daysOfTheWeekP, millisecondsOneDay, months, typesOfPreaching } from '@monorepo/helpers';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { Hr } from 'src/components/_commons';
import { NotificationsAssignments } from './commons/NotificationsAssignments';
import { NotificationsSettings } from './commons/NotificationsSettings';
import { NotificationsSpecialOptions } from './commons/NotificationsSpecialOptions';
import { NotificationsTitle } from './commons/NotificationsTitle';
import { typeNotificationsDoc, typeNotificationSetting, typePublicMinistryDoc, typeUser } from '@monorepo/models';
import { useGetIsDarkMode } from 'src/custom-hooks';

type propsType = {
    notificationsDoc: typeNotificationsDoc;
    publicMinistryDoc: typePublicMinistryDoc;
    setRefreshCounter: Dispatch<SetStateAction<number>>;
    users: typeUser[];
};

export const NotificationsPublicMinistry: FC<propsType> = ({
    notificationsDoc, publicMinistryDoc, setRefreshCounter, users
}) => {
    const [head1Id, setHead1Id] = useState<number | null | undefined>(notificationsDoc.pmHeadOfDepartmentId);
    const [newNotificationSettings, setNewNotificationSettings] = useState<typeNotificationSetting[]>(notificationsDoc.publicMinistry);
    const [sendFeedbackForm, setSendFeedbackForm] = useState(!!notificationsDoc.publicMinistrySendFeedback);
    const [sendForecast, setSendForecast] = useState(!!notificationsDoc.publicMinistrySendForecast);
    const [sendTerritories, setSendTerritories] = useState(!!notificationsDoc.publicMinistrySendTerritories);
    const type = 'public-ministry';

    return (
        <div>
            <NotificationsTitle
                type={type}
            />
            <NotificationsSpecialOptions
                cancelHandler={() => {
                    setSendFeedbackForm(!!notificationsDoc.publicMinistrySendFeedback);
                    setSendForecast(!!notificationsDoc.publicMinistrySendForecast);
                    setSendTerritories(!!notificationsDoc.publicMinistrySendTerritories);
                }}
                getNewNotificationsDoc={() => {
                    const sc = structuredClone(notificationsDoc)
                    sc.publicMinistrySendFeedback = sendFeedbackForm;
                    sc.publicMinistrySendForecast = sendForecast;
                    sc.publicMinistrySendTerritories = sendTerritories;
                    return sc;
                }}
                options={[
                    {
                        changeOption: () => setSendForecast(x => !x),
                        label: 'Enviar el pronóstico del tiempo a los Capitanes de las Salidas junto con el recordatorio (solo si el recordatorio ocurre el mismo día)',
                        originalValue: !!notificationsDoc.publicMinistrySendForecast,
                        value: sendForecast
                    },
                    {
                        changeOption: () => setSendTerritories(x => !x),
                        label: 'Enviar territorios a los Capitanes de las Salidas de Casa en Casa (solo si el recordatorio ocurre el mismo día)',
                        originalValue: !!notificationsDoc.publicMinistrySendTerritories,
                        value: sendTerritories
                    },
                    {
                        changeOption: () => setSendFeedbackForm(x => !x),
                        label: 'Enviar formulario de territorio cubierto a los Capitanes de las Salidas de Casa en Casa (solo si el recordatorio ocurre el mismo día)',
                        originalValue: !!notificationsDoc.publicMinistrySendFeedback,
                        value: sendFeedbackForm
                    }
                ]}
                setRefreshCounter={setRefreshCounter}
                type={type}
            />
            <NotificationsSettings
                head1Id={head1Id}
                head1OriginalId={notificationsDoc.pmHeadOfDepartmentId}
                head2Id={null}
                head2OriginalId={null}
                notificationsDoc={notificationsDoc}
                notificationSettings={notificationsDoc.publicMinistry}
                newNotificationSettings={newNotificationSettings}
                setNewNotificationSettings={setNewNotificationSettings}
                setRefreshCounter={setRefreshCounter}
                type={type}
                updateHead1Id={setHead1Id}
                updateHead2Id={() => {}}
                users={users}
            />

            <Hr classes={'my-5'} />

            <Assignments
                publicMinistryDoc={publicMinistryDoc}
            />
        </div>
    );
}

const Assignments = ({ publicMinistryDoc }: { publicMinistryDoc: typePublicMinistryDoc; }) => {
    const isDarkMode = useGetIsDarkMode();
    const monthlyServices = publicMinistryDoc.monthlyServices
        .filter(ms => new Date(ms.year, ms.month).getTime() >= new Date(new Date().getFullYear(), new Date().getMonth()).getTime() - millisecondsOneDay)
    ;

    return (
        <NotificationsAssignments
            content={
                <div className={'my-3 small'}>
                    {!!monthlyServices.length ?
                        monthlyServices.map((ms, i) =>
                            <div key={`${ms.year}_${ms.month}`}>
                                <div className={`${isDarkMode ? 'bg-dark' : ''} border rounded p-3 my-4`}>
                                    <h5 className={'text-center border-bottom pb-3 mb-4'}>
                                        {months[ms.month].toUpperCase()} {ms.year}
                                    </h5>
                                    {ms.services
                                     .filter(s => !!s.captain1)
                                     .sort((s1, s2) => s1.captain1!.name!.localeCompare(s2.captain1!.name!))
                                     .map(s =>
                                        <div key={s.id}>
                                            <li key={i} className={'mt-2'}>
                                                <span className={'fw-bold'}>{s.captain1!.name} {s.captain1!.lastName}</span> <span className={s.captain1!.phoneNumber ? 'bg-success text-white' : 'bg-general-red text-white'}>(Número {s.captain1!.phoneNumber || 'no cargado'})</span>
                                                {s.captain2 ? <> con suplente <span className={'fw-bold'}>{s.captain2!.name} {s.captain2!.lastName}</span> <span className={s.captain2!.phoneNumber ? 'bg-success text-white' : 'bg-general-red text-white'}>(Número {s.captain2!.phoneNumber || 'no cargado'})</span></> : ''}
                                                <span className={'fw-bold'}>{s.weekday !== null ? ` los ${daysOfTheWeekP[s.weekday]} ` : ` el día ${s.day} `} a las {s.hour.toString().padStart(2, '0')}:{s.minutes.toString().padStart(2, '0')} hs. </span>
                                                ({typesOfPreaching.find(tp => tp.type === s.typeOfPreaching)!.label}{!!s.groups.length ? ' de grupos' : ''})
                                                {/* {s.address1 ? ` desde ${s.address1}` : ''} {s.address2 ? `o desde ${s.address2}` : ''} */}
                                                {s.isCircuitOverseer ? ' durante la Visita' : ''}
                                            </li>
                                        </div>
                                    )}
                                </div>
                                
                            </div>
                        )
                        :
                        <>No hay ninguna asignación</>
                    }
                </div>
            }
        />
    );
}
